/**
 * AssignmentsDialog — Property-Zuweisungen eines Subjekts (User ODER Firma) verwalten.
 * Toggle in der Picker-Liste löst sofort Assign/Revoke aus (B2: greift per-Request,
 * kein Token-Revoke nötig). Verdrahtet Queries/Mutations; die reine Auswahl-Logik
 * steckt im testbaren PropertyPicker.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { useApi, toApiError } from '@/shared/api';
import { PropertyPicker } from './PropertyPicker';
import { useAssignableProperties } from './useAssignableProperties';
import { assignmentKeys } from './queries';

export interface AssignmentSubject {
  kind: 'user' | 'company';
  id: string;
  label: string;
}

export function AssignmentsDialog({
  subject,
  onOpenChange,
}: {
  subject: AssignmentSubject | null;
  onOpenChange: (open: boolean) => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const open = subject !== null;
  const filter = subject
    ? subject.kind === 'user'
      ? { userId: subject.id }
      : { companyId: subject.id }
    : {};

  const properties = useAssignableProperties(open);
  const assignments = useQuery({
    queryKey: assignmentKeys.list(filter),
    queryFn: () => api.propertyAssignments.list(filter),
    enabled: open,
  });

  // Prefix-Invalidation statt list(filter): der Filter wird {} sobald das Dialog schließt (subject=null),
  // eine noch laufende Mutation würde sonst den falschen (leeren) Key invalidieren → stale Cache (globaler staleTime 30s).
  const invalidate = () => queryClient.invalidateQueries({ queryKey: assignmentKeys.all });
  const onError = async (err: unknown) => {
    const e = await toApiError(err);
    toast.error(e.detail ?? e.title);
  };

  const assign = useMutation({
    mutationFn: (propertyId: string) => api.propertyAssignments.assign({ propertyId, ...filter }),
    onSuccess: invalidate,
    onError,
  });
  const revoke = useMutation({
    mutationFn: (assignmentId: string) => api.propertyAssignments.revoke(assignmentId),
    onSuccess: invalidate,
    onError,
  });

  const byProperty = new Map((assignments.data ?? []).map((a) => [a.propertyId, a.id]));
  const selectedIds = Array.from(byProperty.keys());
  const busy = assign.isPending || revoke.isPending;

  const toggle = (propertyId: string) => {
    const existing = byProperty.get(propertyId);
    if (existing) revoke.mutate(existing);
    else assign.mutate(propertyId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apartment-Zuweisungen</DialogTitle>
          <DialogDescription>
            {subject?.kind === 'company'
              ? `Apartments für „${subject.label}" — alle Firma-Mitglieder erben diese Zuweisung.`
              : `Apartments für ${subject?.label ?? ''}. Änderungen wirken sofort.`}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {properties.isLoading || assignments.isLoading ? (
            <p className="text-ink-faint py-4 text-center text-sm">Wird geladen…</p>
          ) : (
            <PropertyPicker
              properties={properties.data ?? []}
              selectedIds={selectedIds}
              onToggle={toggle}
              disabledIds={busy ? (properties.data ?? []).map((p) => p.id) : []}
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Fertig
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
